<script>

    import { onMount, untrack } from 'svelte';
    import { _, locale } from 'svelte-i18n';

    import 'vanillajs-datepicker/css/datepicker.min.css';
    import { Datepicker } from 'vanillajs-datepicker';
    import nl from 'vanillajs-datepicker/locales/nl';
    Object.assign(Datepicker.locales, nl);

    import { DateTime } from 'luxon';



    let { value = null, availableDates = [], busyTimeframes = {}, onDateChange, onViewChanged, onSkip, skipLabel } = $props();

    /* The calendar runs from today to a year out. The API caps its answers
       at the salon's own booking window anyway; this ceiling keeps the
       next-month button and the arrow keys from paging into decades of
       months that would each cost a request for an answer known in advance:
       empty. */

    const HORIZON_DAYS = 365;

    let unavailableDates = $derived(Object.keys(busyTimeframes));

    let datepicker;
    let previousAvailableDatesLength = 0;
    let previousUnavailableDatesLength = 0;
    let previousLocale = null;
    let elem;

    /* Patch ARIA attributes onto the datepicker's rendered DOM.
       Called after mount, month navigation, date selection, and locale changes
       because the library renders bare <span> elements with no semantic markup. */
    function patchA11y() {
        if (!elem) return;

        // Make prev/next buttons keyboard accessible
        for (const [selector, key] of [['.prev-btn', 'datepicker_prev_month'], ['.next-btn', 'datepicker_next_month']]) {
            const btn = elem.querySelector(selector);
            if (!btn) continue;
            btn.setAttribute('aria-label', $_(key));
            btn.setAttribute('tabindex', '0');
            if (!btn._a11yKeydown) {
                btn._a11yKeydown = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        btn.click();
                    }
                };
                btn.addEventListener('keydown', btn._a11yKeydown);
            }
        }

        // Hide day-of-week headers (ma, di, wo…) from the accessible tree —
        // the full date labels on focusable children already convey the day.
        const daysOfWeek = elem.querySelector('.days-of-week');
        if (daysOfWeek) daysOfWeek.setAttribute('aria-hidden', 'true');

        // Label each date cell and insert focusable children.
        // We nest interactive elements inside cells so the library (which only
        // iterates direct grid children) never encounters our injected elements.
        const cells = elem.querySelectorAll('.datepicker-cell:not(.next):not(.prev)');
        cells.forEach(cell => {
            const dataDate = cell.getAttribute('data-date');
            if (!dataDate) return;

            const timestamp = parseInt(dataDate);
            const dt = DateTime.fromMillis(timestamp);
            const label = dt.setLocale($locale).toLocaleString(DateTime.DATE_FULL);

            cell.setAttribute('role', 'gridcell');

            const isDisabled = cell.classList.contains('disabled') || cell.classList.contains('date-disabled');
            const isSelected = cell.classList.contains('selected');

            // Remove previous a11y children
            cell.querySelector('.a11y-focus')?.remove();
            cell.querySelector('.a11y-skip-to-time')?.remove();

            if (!isDisabled) {
                // Insert a focusable child that receives keyboard focus
                const focus = document.createElement('span');
                focus.className = 'a11y-focus sr-only';
                focus.setAttribute('role', 'button');
                focus.setAttribute('tabindex', '0');
                focus.setAttribute('aria-label', label);
                focus.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        cell.click();
                        // After selection, focus the skip link so the user can
                        // jump straight to the time picker
                        requestAnimationFrame(() => {
                            elem?.querySelector('.a11y-skip-to-time')?.focus();
                        });
                    }
                });
                cell.appendChild(focus);

                // Insert skip link inside the selected cell, after the focusable child
                if (isSelected && onSkip && skipLabel) {
                    const skip = document.createElement('a');
                    skip.href = '#';
                    skip.className = 'a11y-skip-to-time sr-only';
                    skip.textContent = skipLabel;
                    skip.addEventListener('click', (e) => {
                        e.preventDefault();
                        onSkip();
                    });
                    cell.appendChild(skip);
                }
            }
        });
    }

    onMount(() => {
        elem = document.getElementById('date-picker');

        datepicker = new Datepicker(elem, {
            language: $locale,
            maxView: 0,
            minDate: DateTime.now().toJSDate(), 
            maxDate: DateTime.now().plus({ days: HORIZON_DAYS }).endOf('day').toJSDate(),

            datesDisabled: (date) => {
                return DateTime.now().startOf('day') > date;
            },

            beforeShowDay: (date) => {
                const isoDate = DateTime.fromJSDate(date).toISODate();

                let classes = 'date-disabled';
                if (availableDates.includes(isoDate)) {
                    classes = 'date-available';
                } else if (unavailableDates.includes(isoDate)) {
                    classes = 'date-unavailable';
                }

                return {
                    classes: classes,
                };
            }
        });

        elem.addEventListener('changeDate', (event) => {
            onDateChange(DateTime.fromJSDate(event.detail.date).toISODate());
        });

        function handleChangeMonth(event) {
            viewChanged(DateTime.fromJSDate(event.detail.viewDate));
        }

        // Store reference so we can remove it later
        elem._handleChangeMonth = handleChangeMonth;
        elem.addEventListener('changeMonth', handleChangeMonth);

        elem.addEventListener('changeDate', () => patchA11y());
        elem.addEventListener('changeMonth', () => patchA11y());

        if (value) {
            datepicker.setDate(DateTime.fromISO(value).toJSDate());
        }

        patchA11y();

        function viewChanged(date) {
            let month = date.startOf('month').toISODate();
            let last = date.endOf('month').toISODate();

            /* Only trigger is last is today or later */
            
            if (DateTime.now().toISODate() > last) {
                return;
            }

            /* Make sure month is not in the past */
            
            if (DateTime.now().toISODate() > month) {
                month = DateTime.now().toISODate();
            }

            /* Start is the number of days that month is since today */
            let start = Math.floor(DateTime.fromISO(month).diff(DateTime.now().startOf('day'), 'days').days);

            /* Limit is the number of days between month and last */
            let limit = Math.floor(DateTime.fromISO(last).diff(DateTime.fromISO(month), 'days').days) + 1;

            onViewChanged({
                month, start, limit
            });
        }
    });

    $effect(() => {
        // Only re-render if dates were actually added (not just array reference change)
        if (availableDates.length !== previousAvailableDatesLength || unavailableDates.length !== previousUnavailableDatesLength) {
            previousAvailableDatesLength = availableDates.length;
            previousUnavailableDatesLength = unavailableDates.length;

            if (datepicker && datepicker.picker && elem) {
                // Manually update CSS classes without calling render()
                const dateCells = elem.querySelectorAll('.datepicker-cell:not(.next):not(.prev)');
                dateCells.forEach(cell => {
                    const dataDate = cell.getAttribute('data-date');
                    if (dataDate) {
                        const timestamp = parseInt(dataDate);
                        const isoDate = DateTime.fromMillis(timestamp).toISODate();

                        // Remove old classes
                        cell.classList.remove('date-available', 'date-unavailable', 'date-disabled');

                        // Add new class based on availability
                        if (availableDates.includes(isoDate)) {
                            cell.classList.add('date-available');
                        } else if (unavailableDates.includes(isoDate)) {
                            cell.classList.add('date-unavailable');
                        } else {
                            cell.classList.add('date-disabled');
                        }
                    }
                });

                patchA11y();
            }
        }
    });

    $effect(() => {
        // Only update locale if it actually changed
        if (datepicker && $locale && $locale !== previousLocale) {
            previousLocale = $locale;

            // Use untrack to prevent beforeShowDay callback's reactive reads
            // (availableDates, unavailableDates) from becoming dependencies of this effect
            untrack(() => {
                const savedViewDate = datepicker.picker.viewDate;
                datepicker.setOptions({ language: $locale });
                datepicker.picker.viewDate = savedViewDate;
                datepicker.refresh();
                patchA11y();
            });
        }
    });


</script>

<div id="date-picker" aria-label={$_('datepicker_label')}></div>

<style>

    /* Datepicker */

    #date-picker {
        display: flex;
    }

    #date-picker :global(.datepicker-picker) {
        padding: 1em;
        background-color: var(--box-color);
        border-radius: 8px;
    }


    /* Controls */

    #date-picker :global(.datepicker-title) {
        background-color: var(--box-color);
    }

    #date-picker :global(.datepicker-controls .button) {
        background-color: var(--box-color);
        color: var(--text-color);
        transition: opacity 0.2s ease;
    }

    #date-picker :global(.datepicker-header .datepicker-controls .button:hover) {
        background-color: var(--box-hover-color);
        color: var(--text-color);
    }

    #date-picker :global(.datepicker-controls .button:disabled) {
        opacity: 0;
    }


    /* Date */

    #date-picker :global(.datepicker-cell:not(.disabled):hover) {
        background: none;
    }

    #date-picker :global(.datepicker-cell.focused:not(.selected)) {
        background: none;
    }

    #date-picker :global(.datepicker-cell:focus-within) {
        outline: 2px solid var(--accent-background);
        outline-offset: -2px;
        border-radius: 50%;
    }

    /* Available, unavailable, and disabled dates */

    #date-picker :global(.date-unavailable) {
        text-decoration: line-through;
    }

    #date-picker :global(.date-disabled) {
        opacity: 0.2;
        text-decoration: none;
    }


    /* Hide cells that are not in the current month */

    #date-picker :global(.datepicker-cell.next), 
    #date-picker :global(.datepicker-cell.prev) {
        visibility: hidden;
        pointer-events: none;
    }
    #date-picker :global(.datepicker-cell.next) {
        display: none;
    }

    #date-picker :global(.datepicker-cell.disabled) {
        /* visibility: hidden; */
        opacity: 0.2;
        text-decoration: none;
    }

    #date-picker :global(.days-of-week .dow) {
        margin-bottom: 0.8em;
        opacity: 0.6
    }

    /* Selected date */

    #date-picker :global(.datepicker-cell.selected), 
    #date-picker :global(.datepicker-cell.selected:hover) {
        background-color: var(--text-color);
        color: var(--background-color);

        box-sizing: border-box;
        border-radius: 50%;
        border: 3px solid var(--background-color);
        box-shadow: 0 0 0 2px var(--accent-contrast);

        opacity: 1;
    }

</style>